import {
	Dimension,
	FeatureEntity,
	PaymentProvider,
	PlanFamily,
	PlanStatus,
	SupportedFeature,
	SupportedPlan,
} from "@src/gql/graphql";
import { z } from "zod";

// Base schema for all feature states
export const featureStateSchema = z.object({
	feature: z.nativeEnum(SupportedFeature),
	available: z.boolean(),
});

export type FeatureState = z.infer<typeof featureStateSchema>;

// Schema for metered features
export const meteredFeatureStateSchema = featureStateSchema.extend({
	current: z.number(),
	quota: z.number(),
	dimension: z.nativeEnum(Dimension),
});

export type MeteredFeatureState = z.infer<typeof meteredFeatureStateSchema>;

// Schema for metered per entity features
export const meteredPerEntityFeatureStateSchema =
	meteredFeatureStateSchema.extend({
		entity: z.nativeEnum(FeatureEntity),
		entityId: z.string(),
	});

export type MeteredPerEntityFeatureState = z.infer<
	typeof meteredPerEntityFeatureStateSchema
>;

// Schema for metered per period features
export const meteredPerPeriodFeatureStateSchema = featureStateSchema.extend({
	period: z.string(),
	periodReset: z.string(),
	current: z.number(),
	quota: z.number(),
});

export type MeteredPerPeriodFeatureState = z.infer<
	typeof meteredPerPeriodFeatureStateSchema
>;

export const meteredPerPeriodWithBalanceFeatureStateSchema =
	meteredPerPeriodFeatureStateSchema.extend({
		balance: z.number(),
		balanceUsedThisPeriod: z.number(),
		featureAvailable: z.boolean(),
	});

// Define discriminated union for feature state types
export const anyFeatureStateSchema = z.union([
	meteredPerEntityFeatureStateSchema,
	meteredPerPeriodWithBalanceFeatureStateSchema,
	meteredPerPeriodFeatureStateSchema,
	meteredFeatureStateSchema,
	featureStateSchema,
]);

export type AnyFeatureState = z.infer<typeof anyFeatureStateSchema>;

export const externalStateSchema = z
	.object({
		id: z.string().nullable(),
		url: z.string().nullable(),
		userId: z.string().nullable(),
		terms: z
			.object({
				id: z.string().nullable(),
				name: z.string().nullable(),
				type: z.string().nullable(),
				url: z.string().nullable(),
			})
			.nullable(),
	})
	.nullable();

export type ExternalState = z.infer<typeof externalStateSchema>;

const featuresSchema = z.preprocess((input) => {
	if (!Array.isArray(input)) {
		return [];
	}

	return input.flatMap((feature, index) => {
		const parsedFeature = anyFeatureStateSchema.safeParse(feature);
		if (parsedFeature.success) {
			return [parsedFeature.data];
		}

		console.warn("[plan] Dropping unsupported feature state", {
			index,
			feature,
			error: parsedFeature.error.issues,
		});
		return [];
	});
}, z.array(anyFeatureStateSchema));

export const planSchema = z.object({
	plan: z.nativeEnum(SupportedPlan),
	planFamily: z.nativeEnum(PlanFamily),
	status: z.nativeEnum(PlanStatus),
	provider: z.nativeEnum(PaymentProvider),
	features: featuresSchema,
	external: externalStateSchema,
	trialEnd: z.string().nullable(),
	renewsAt: z.string().nullable(),
	currentPeriodAvailableExtraUsageCredits: z.number().optional(),
	extraUsageCredits: z.number().optional(),
	confirmedTotalUsedCredits: z.number().optional(),
	nextBalanceCloseAt: z.string().nullable().optional(),
});

export type Plan = z.infer<typeof planSchema>;

export const graduatedBetaPlanStateSchema = z.object({
	internalUserId: z.string().nullable(),
	provider: z.nativeEnum(PaymentProvider),
	renewsAt: z.string().nullable(),
	status: z.nativeEnum(PlanStatus),
	trialEndsAt: z.string().nullable(),
});

export const graduatedBetaResultSchema = z.object({
	wasMigrated: z.boolean(),
	plan: z
		.object({
			position: z.number(),
			state: graduatedBetaPlanStateSchema,
		})
		.nullable(),
});

export type GraduatedBetaResult = z.infer<typeof graduatedBetaResultSchema>;
